import rootRender from '@react/utils/root-render';
import {
  Color,
  BASE_COLORS,
  NAVY_COLORS,
  GREYSCALE_COLORS,
  MENU_ITEMS,
  HEADINGS,
  BODY_COPY,
  LABELS,
  SPACING,
  COLOR_TOKENS,
  ICONS,
  BUTTONS,
  ButtonEntity,
  DARK_BUTTONS,
  TAGS,
  PILLS,
} from '@react/pages/styleguide.data';
import { highlightAll } from 'prismjs';
import { ComponentProps, Fragment, useLayoutEffect } from 'react';
import copyToClipboard from '@/js/general/copy-to-clipboard';

const renderName = (name: string) => name.split('-').join(' ');

const renderColorItems = (colors: Color[]) => {
  return colors.map(color => (
    <div
      key={color.hex}
      className="styleguide__grid-item"
      style={{
        backgroundColor: color.hex,
        color: color?.lightColor ? '#1f1f2c' : null,
      }}
    >
      <Code language="scss" code={color.name} />
      <button
        type="button"
        className="styleguide__grid-item-copy"
        onClick={() => copyToClipboard(color.hex)}
      >
        {color.hex}
      </button>
    </div>
  ));
};

function Code({
  code,
  language,
}: Readonly<{ code: string; language: string }>) {
  useLayoutEffect(() => {
    highlightAll();
  }, []);

  return (
    <button
      type="button"
      className="code"
      onClick={() => copyToClipboard(code)}
    >
      <pre>
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </button>
  );
}

const buttonStringWithSwipe = `<button class="styleguide__button styleguide__button--primary">
    <span class="inner">
        <span class="text">primary</span>
        <span class="hover">primary</span>
    </span>
    <i class="icon-arrow-right"></i>
</button>`;

const buttonStringWithoutSwipe = `<button class="styleguide__button styleguide__button--tertiary">
    <span>primary</span>
    <i class="icon-arrow-right"></i>
</button>`;

const anchorButton = `<button class="styleguide__button styleguide__button--anchor">
    <span>anchor</span>
</button>`;

interface Button extends ComponentProps<'button'> {
  button: ButtonEntity;
}

const Button = ({ button, ...props }: Button) => {
  const name = renderName(button.name);
  return (
    <button
      className={`styleguide__button styleguide__button--${button.name}`}
      {...props}
    >
      {!button?.noText ? (
        <span className="inner">
          <span className="text">{name}</span>
          {button?.textSwipe ? <span className="hover">{name}</span> : null}
        </span>
      ) : null}
      {button.icon ? <i className={`icon-${button.icon}`} /> : null}
    </button>
  );
};

const ExitButton = ({ button, ...props }: Button) => {
  return (
    <button
      className={`styleguide__button styleguide__button--${button.name}`}
      {...props}
    >
      <span className="inner">
        <span className="text">
          <i className={`icon-${button.icon}`} />
        </span>
        <span className="hover">
          <i className={`icon-${button.icon}`} />
        </span>
      </span>
    </button>
  );
};

export default function Styleguide() {
  return (
    <main className="styleguide">
      <nav className="styleguide__menu">
        <ul>
          {MENU_ITEMS.map(item => (
            <li key={item.id}>
              <a href={`#${item.id}`}>{item.name}</a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="styleguide__content">
        <h1 className="styleguide__main-heading">Styleguide</h1>
        <h2 id="colours" className="styleguide__heading">
          Colours
        </h2>
        <section className="styleguide__section">
          <h4 className="styleguide__sub-heading">Brand</h4>
          <div className="styleguide__grid">
            {renderColorItems(BASE_COLORS)}
          </div>
        </section>
        <section className="styleguide__section">
          <h4 className="styleguide__sub-heading">Navy</h4>
          <div className="styleguide__grid">
            {renderColorItems(NAVY_COLORS)}
          </div>
        </section>
        <section className="styleguide__section">
          <h4 className="styleguide__sub-heading">Greyscale</h4>
          <div className="styleguide__grid">
            {renderColorItems(GREYSCALE_COLORS)}
          </div>
        </section>
        <section className="styleguide__section">
          <h4 className="styleguide__sub-heading" id="tokens">
            Tokens
          </h4>
          <p className="styleguide__description">
            There is a large list of design tokens, these are referenced in the
            design files and should be the only colour variables used in
            components. Do not use the root color variables (eg
            "$color-brand-red"). If needed new tokens can be added.
          </p>
          <div className="styleguide__spacing">
            {COLOR_TOKENS.map(token => (
              <div className="styleguide__spacing-item" key={token.name}>
                <div>{token.name}</div>
                <ul>
                  {token.tokens.map(text => (
                    <li key={text}>
                      <Code language="scss" code={text} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="headings" className="styleguide__heading">
            Headings
          </h2>
          <div className="styleguide__headings">
            {HEADINGS.map((heading, i) => {
              let DynamicTag = `h${i + 1}` as keyof JSX.IntrinsicElements;
              if (i >= 6) DynamicTag = 'p';
              return (
                <Fragment key={heading.name}>
                  <DynamicTag>{heading.name}</DynamicTag>
                  <Code language="scss" code={heading.placeholder} />
                </Fragment>
              );
            })}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="body" className="styleguide__heading">
            Body
          </h2>
          <div className="styleguide__headings">
            {BODY_COPY.map(body => {
              return (
                <Fragment key={body.name}>
                  <p
                    className={`styleguide__body styleguide__body--${body.modifier}`}
                  >
                    {body.name}
                  </p>
                  <Code language="scss" code={body.placeholder} />
                </Fragment>
              );
            })}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="labels" className="styleguide__heading">
            Labels
          </h2>
          <div className="styleguide__headings">
            {LABELS.map(label => {
              return (
                <Fragment key={label.name}>
                  <label
                    className={`styleguide__label styleguide__label--${label.modifier}`}
                  >
                    {label.name}
                  </label>
                  <Code language="scss" code={label.placeholder} />
                </Fragment>
              );
            })}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="spacing" className="styleguide__heading">
            Spacing
          </h2>
          <p className="styleguide__description">
            These are the placeholders used to define spacing between component
            blocks. Any combination of top or bottom spacing can be used. The
            values differ between mobile and desktop breakpoints.
          </p>
          <div className="styleguide__spacing">
            {SPACING.map(space => {
              return (
                <div className="styleguide__spacing-item" key={space.name}>
                  <div>{space.name}</div>
                  <ul>
                    {space.placeholders.map(pl => (
                      <li key={pl}>
                        <Code language="scss" code={pl} />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="icons" className="styleguide__heading">
            Icons
          </h2>
          <div className="styleguide__icons">
            {ICONS.map(icon => (
              <button
                type="button"
                key={icon}
                className="styleguide__icons-item"
                onClick={() =>
                  copyToClipboard(`<i className="icon-${icon}"></i>`)
                }
              >
                <i className={`icon-${icon}`} />
                <div>{renderName(icon)}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="styleguide__section">
          <h2 id="buttons" className="styleguide__heading">
            Buttons
          </h2>
          <div className="styleguide__buttons">
            {BUTTONS.map(button => {
              const DynamicButton =
                button.name === 'exit' ? ExitButton : Button;
              return (
                <div className="styleguide__button-group" key={button.name}>
                  {<DynamicButton button={button} />}
                  {<DynamicButton button={button} disabled />}
                  <Code
                    language="scss"
                    code={`@extend %button-${button.name};`}
                  />
                </div>
              );
            })}
          </div>
        </section>
        <section className="styleguide__section styleguide__section--dark">
          <h4 className="styleguide__sub-heading">
            Buttons on dark backgrounds
          </h4>
          <div className="styleguide__buttons">
            {DARK_BUTTONS.map(button => {
              const DynamicButton =
                button.name === 'exit' ? ExitButton : Button;
              return (
                <div
                  className="styleguide__button-group"
                  key={`dark-${button.name}`}
                >
                  {<DynamicButton button={button} />}
                  {<DynamicButton button={button} disabled />}
                  <Code
                    language="scss"
                    code={`@extend %button-${button.name};`}
                  />
                </div>
              );
            })}
          </div>
        </section>
        <section className="styleguide__section">
          <h4 className="styleguide__sub-heading">Button with text swipe</h4>
          <Code language="html" code={buttonStringWithSwipe} />
          <h4 className="styleguide__sub-heading">Button without text swipe</h4>
          <Code language="html" code={buttonStringWithoutSwipe} />
          <h4 className="styleguide__sub-heading">Anchor button</h4>
          <Code language="html" code={anchorButton} />
        </section>

        <section className="styleguide__section">
          <h2 id="tags" className="styleguide__heading">
            Tags
          </h2>
          <div className="styleguide__tags">
            {TAGS.map(tag => {
              return (
                <div className="styleguide__tag-group" key={tag}>
                  <div className={`styleguide__tag styleguide__tag--${tag}`}>
                    {tag}
                  </div>
                  <Code language="scss" code={`@extend %tag-${tag};`} />
                </div>
              );
            })}
          </div>
          <h4 className="styleguide__sub-heading">Pills</h4>
          <div className="styleguide__tags">
            {PILLS.map(pill => {
              return (
                <div className="styleguide__tag-group" key={pill}>
                  <button
                    className={`styleguide__pill styleguide__pill--${pill}`}
                  >
                    {renderName(pill)}
                  </button>
                  <Code language="scss" code={`@extend %${pill};`} />
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </main>
  );
}

rootRender('react-styleguide', <Styleguide />);
